import { useState } from 'react';

/**
 * Shared inline validation for modal forms.
 *
 * Every field marked with a * in the form should have a rule here, so the
 * asterisks and the actual check can't drift apart. Errors are shown against
 * the field itself instead of only as a toast, so the user can see which one
 * needs fixing.
 *
 * rules: { fieldName: { label, positive } }
 *   label    — name shown in the message ("Vehicle is required")
 *   positive — also require a number greater than 0 ("Cost must be greater than 0")
 *
 * Usage:
 *   const { errors, validate, clearError, invalid } = useFormErrors({
 *     vehicle_id: { label: 'Vehicle' },
 *     cost: { label: 'Cost', positive: true },
 *   });
 *   ...
 *   if (!validate(form)) return;                       // in save()
 *   <select className={invalid('vehicle_id')} ... />   // red border
 *   <FieldError msg={errors.vehicle_id}/>              // message under the field
 */
export function useFormErrors(rules) {
  const [errors, setErrors] = useState({});

  const check = (form) => {
    const found = {};
    for (const [key, rule] of Object.entries(rules)) {
      const value = form[key];
      const isEmpty = value === '' || value === null || value === undefined;
      if (isEmpty) {
        found[key] = `${rule.label} is required`;
      } else if (rule.positive && !(parseFloat(value) > 0)) {
        found[key] = `${rule.label} must be greater than 0`;
      }
    }
    return found;
  };

  // Returns true when the form is clean; otherwise marks the offending fields.
  const validate = (form) => {
    const found = check(form);
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  // Drop a field's error as soon as the user edits it.
  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const reset = () => setErrors({});

  const invalid = (key) => (errors[key] ? 'ds-invalid' : '');

  return { errors, validate, clearError, reset, invalid };
}

export default useFormErrors;
